import { useCallback, useEffect, useRef } from 'react';

const usePolling = (poll: () => Promise<void>, interval = 10000, isPollingDisabled: boolean) => {
  const isPollingEnabled = useRef(true);
  const pollTimerRef = useRef<number>();

  const queueNextPoll = useCallback(() => {
    if (!isPollingEnabled.current || document.hidden) {
      return;
    }

    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
    }
    pollTimerRef.current = setTimeout(async () => {
      if (!isPollingEnabled.current || document.hidden) {
        return;
      }
      await poll();
      queueNextPoll();
    }, interval);
  }, [poll, interval]);

  useEffect(() => {
    const onVisibilityChange = async () => {
      if (!isPollingEnabled.current || document.hidden || isPollingDisabled) {
        return;
      }
      await poll();
      queueNextPoll();
    };
    window.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('visibilitychange', onVisibilityChange);
      clearTimeout(pollTimerRef.current);
    };
  }, [isPollingDisabled]);

  const setPollingEnabled = async (isEnabled: boolean) => {
    isPollingEnabled.current = isEnabled;
    if (!isPollingEnabled.current || document.hidden) {
      return;
    }
    await poll();
    queueNextPoll();
  };

  return { initializePolling: queueNextPoll, setPollingEnabled };
};

export default usePolling;
